import React, { useCallback, useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { apiRequest, API_BASE_URL } from "../api/client";
import { cleanAssistantText } from "../utils/assistantText";
import { shouldSubmitOnEnter } from "../utils/inputDevice";

// ─── helpers ────────────────────────────────────────────────────────────────

function resolveAudioUrl(url) {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return API_BASE_URL.replace(/\/api\/v1$/, "") + url;
}

function audioApiPath(url) {
  if (!url) return "";
  try {
    const parsed = new URL(resolveAudioUrl(url));
    return parsed.pathname.replace(/^\/api\/v1/, "") + parsed.search;
  } catch (_) {
    return url.replace(/^\/api\/v1/, "");
  }
}

async function playProtectedAudio(url) {
  const path = audioApiPath(url);
  if (!path) return null;
  const blob = await apiRequest(path);
  const objectUrl = URL.createObjectURL(blob);
  const audio = new Audio(objectUrl);
  const cleanup = () => URL.revokeObjectURL(objectUrl);
  audio.addEventListener("ended", cleanup, { once: true });
  audio.addEventListener("error", cleanup, { once: true });
  await audio.play();
  return audio;
}

// ─── sub-components ──────────────────────────────────────────────────────────

function VoicePulse({ active }) {
  return (
    <div className="relative flex items-center justify-center w-20 h-20">
      {active && (
        <>
          <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-30 animate-ping" />
          <span className="absolute inline-flex h-16 w-16 rounded-full bg-red-400 opacity-20 animate-ping" style={{ animationDelay: "0.3s" }} />
        </>
      )}
      <div className={`relative flex items-center justify-center w-14 h-14 rounded-full transition-all duration-200 ${
        active ? "bg-red-500 shadow-lg shadow-red-500/40 scale-110" : "bg-[#202123] hover:bg-[#10a37f] hover:shadow-lg hover:shadow-[#10a37f]/30"
      }`}>
        <span className="material-symbols-outlined text-white text-3xl" style={{ fontVariationSettings: "'FILL' 1" }}>
          {active ? "stop" : "mic"}
        </span>
      </div>
    </div>
  );
}

function TurnCard({ turn, playing, onPlay }) {
  const audioUrl = turn.assistant_audio_url;
  const answerText = cleanAssistantText(turn.answer_text);
  const audioRef = useRef(null);
  const objectUrlRef = useRef(null);
  const [audioError, setAudioError] = useState("");

  async function handlePlayPause() {
    if (!audioUrl || !audioRef.current) return;
    setAudioError("");
    if (audioRef.current.paused) {
      try {
        if (!audioRef.current.src) {
          const blob = await apiRequest(audioApiPath(audioUrl));
          objectUrlRef.current = URL.createObjectURL(blob);
          audioRef.current.src = objectUrlRef.current;
        }
        onPlay(turn.voice_turn_id);
        await audioRef.current.play();
      } catch (err) {
        setAudioError(err.message || "Audio gagal diputar. Coba ulangi atau login ulang.");
        onPlay(null);
      }
    } else {
      audioRef.current.pause();
      onPlay(null);
    }
  }

  useEffect(() => {
    if (!playing && audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
    }
  }, [playing]);

  useEffect(() => {
    const audio = audioRef.current;
    return () => {
      if (audio) audio.pause();
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, []);

  return (
    <div className="space-y-3">
      {/* User transcript */}
      <div className="flex justify-end">
        <div className="bg-gray-100 rounded-2xl rounded-tr-sm px-4 py-3 max-w-[80%]">
          <div className="flex items-center gap-2 mb-1">
            <span className="material-symbols-outlined text-gray-400 text-[14px]">mic</span>
            <span className="text-[10px] text-gray-400 font-medium">You</span>
          </div>
          <p className="text-gray-900 text-[15px] leading-relaxed break-words [overflow-wrap:anywhere]">{turn.transcript}</p>
        </div>
      </div>

      {/* AI response */}
      <div className="flex gap-3">
        <div className="w-8 h-8 rounded-lg bg-[#10a37f] flex items-center justify-center flex-shrink-0 mt-1">
          <span className="material-symbols-outlined text-white text-[16px]" style={{ fontVariationSettings: "'FILL' 1" }}>smart_toy</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-gray-400 mb-1">Assistant</p>
          <div className="bg-white border border-gray-100 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
            <p className="text-gray-800 text-[15px] leading-relaxed break-words [overflow-wrap:anywhere]">{answerText}</p>
            {turn.tts_error && (
              <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                Audio suara belum tersedia: {turn.tts_error}
              </div>
            )}
            {audioError && (
              <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {audioError}
              </div>
            )}
            {audioUrl && (
              <div className="mt-3 flex items-center gap-3">
                <button
                  onClick={handlePlayPause}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#10a37f]/10 hover:bg-[#10a37f]/20 text-[#10a37f] text-xs font-semibold transition-colors"
                >
                  <span className="material-symbols-outlined text-[16px]">
                    {playing ? "pause" : "play_arrow"}
                  </span>
                  {playing ? "Pause" : "Putar Suara"}
                </button>
                <audio ref={audioRef} onEnded={() => onPlay(null)} className="hidden" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── main component ───────────────────────────────────────────────────────────

const RECORD_TIMEOUT_MS = 60_000;
const MIN_RECORDING_MS = 900;
const MIN_AUDIO_BYTES = 1200;

export default function VoiceView() {
  const {
    voiceSessionId,
    voiceTurns,
    sendVoiceTurnAction,
    resetVoiceSession,
  } = useOutletContext();

  // recording state (local only — no need to persist)
  const [recording, setRecording] = useState(false);
  const recorderRef    = useRef(null);
  const chunksRef      = useRef([]);
  const recordTimerRef = useRef(null);
  const recordStartedAtRef = useRef(0);

  // processing phase
  const [phase, setPhase] = useState("idle"); // idle | recording | uploading | processing | done | error
  const [errorMsg, setErrorMsg] = useState("");

  // text fallback
  const [textInput, setTextInput]     = useState("");
  const [sendingText, setSendingText] = useState(false);

  // audio playback
  const [playingTurnId, setPlayingTurnId] = useState(null);

  // auto-scroll on new turns
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [voiceTurns]);

  // ── send helpers ─────────────────────────────────────────────────────────────

  async function sendAudioBlob(blob) {
    setPhase("uploading");
    setErrorMsg("");
    try {
      setPhase("processing");
      const formData = new FormData();
      formData.append("audio", blob, "voice.webm");
      const result = await sendVoiceTurnAction(formData);

      setPhase("done");
      setTimeout(() => setPhase("idle"), 2000);

      // Auto-play response audio
      if (result.assistant_audio_url) {
        playProtectedAudio(result.assistant_audio_url)
          .then((audio) => {
            if (!audio) return;
            setPlayingTurnId(result.voice_turn_id);
            audio.onended = () => setPlayingTurnId(null);
          })
          .catch(() => setErrorMsg("Audio suara sudah dibuat, tapi browser gagal memutar otomatis. Tap tombol Putar Suara."));
      }
    } catch (err) {
      const msg = err.name === "AbortError"
        ? "Timeout: server terlalu lama merespons. Coba lagi."
        : err.message || "Terjadi error saat memproses suara";
      setErrorMsg(msg);
      setPhase("error");
    }
  }

  async function sendTextAsVoice() {
    if (!textInput.trim() || sendingText) return;
    const text = textInput.trim();
    setTextInput("");
    setSendingText(true);
    setPhase("processing");
    setErrorMsg("");
    try {
      const formData = new FormData();
      const silentBlob = new Blob([], { type: "audio/webm" });
      formData.append("audio", silentBlob, "voice.webm");
      formData.append("transcript", text);

      const result = await sendVoiceTurnAction(formData);
      setPhase("done");
      setTimeout(() => setPhase("idle"), 1500);

      if (result.assistant_audio_url) {
        playProtectedAudio(result.assistant_audio_url)
          .then((audio) => {
            if (!audio) return;
            setPlayingTurnId(result.voice_turn_id);
            audio.onended = () => setPlayingTurnId(null);
          })
          .catch(() => setErrorMsg("Audio suara sudah dibuat, tapi browser gagal memutar otomatis. Tap tombol Putar Suara."));
      }
    } catch (err) {
      setErrorMsg(err.message || "Gagal mengirim teks");
      setPhase("error");
    } finally {
      setSendingText(false);
    }
  }

  function handleTextInputKeyDown(e) {
    if (!shouldSubmitOnEnter(e)) return;
    e.preventDefault();
    sendTextAsVoice();
  }

  // ── recording ─────────────────────────────────────────────────────────────

  const startRecording = useCallback(async () => {
    if (recording || phase === "processing" || phase === "uploading") return;
    setPhase("recording");
    setErrorMsg("");
    try {
      const stream   = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      recordStartedAtRef.current = Date.now();

      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const durationMs = Date.now() - recordStartedAtRef.current;
        if (durationMs < MIN_RECORDING_MS || blob.size < MIN_AUDIO_BYTES) {
          setErrorMsg("Audio terlalu pendek atau kosong. Rekam minimal 1 detik dan pastikan mikrofon menangkap suara.");
          setPhase("error");
          return;
        }
        sendAudioBlob(blob);
      };

      recorder.start();
      recorderRef.current = recorder;
      setRecording(true);
      recordTimerRef.current = setTimeout(() => stopRecording(), RECORD_TIMEOUT_MS);
    } catch (err) {
      setErrorMsg("Tidak bisa mengakses mikrofon: " + err.message);
      setPhase("error");
    }
  }, [recording, phase]); // eslint-disable-line

  const stopRecording = useCallback(() => {
    clearTimeout(recordTimerRef.current);
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
    setRecording(false);
  }, []);

  useEffect(() => () => {
    clearTimeout(recordTimerRef.current);
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
  }, []);

  // ── derived state ─────────────────────────────────────────────────────────

  const isProcessing = phase === "uploading" || phase === "processing";
  const isBusy       = recording || isProcessing;

  const statusInfo = {
    recording:  { icon: "fiber_manual_record", text: "Merekam... klik Stop saat selesai",   color: "text-red-500" },
    uploading:  { icon: "cloud_upload",        text: "Mengirim audio ke server...",          color: "text-blue-500" },
    processing: { icon: "psychology",          text: "AI sedang memproses jawaban...",       color: "text-[#10a37f]" },
    done:       { icon: "check_circle",        text: "Selesai!",                             color: "text-green-500" },
    error:      { icon: "error",               text: errorMsg,                               color: "text-red-500" },
    idle:       { icon: null,                  text: null,                                   color: "" },
  }[phase];

  return (
    <main className="flex-1 flex flex-col min-h-0 bg-gray-50 overflow-hidden">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-100 shadow-sm flex-shrink-0">
        <div className="flex items-center justify-between h-14 pl-14 pr-4 lg:px-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gray-900 flex items-center justify-center">
              <span className="material-symbols-outlined text-white text-[18px]" style={{ fontVariationSettings: "'FILL' 1" }}>mic</span>
            </div>
            <div>
              <h1 className="font-bold text-gray-900 text-base leading-tight">Voice AI</h1>
              <p className="text-[11px] text-gray-400 hidden sm:block">
                {voiceSessionId
                  ? `${voiceTurns.length} pesan · riwayat tersimpan di Chat`
                  : "Percakapan suara dua arah"}
              </p>
            </div>
          </div>
          {voiceSessionId && (
            <button
              onClick={resetVoiceSession}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-500 hover:bg-gray-50 transition-colors"
            >
              <span className="material-symbols-outlined text-[14px]">refresh</span>
              Sesi Baru
            </button>
          )}
        </div>
      </header>

      {/* Conversation area */}
      <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4">
        <div className="max-w-[700px] mx-auto space-y-5">

          {/* Empty state */}
          {voiceTurns.length === 0 && !isBusy && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-20 h-20 rounded-full bg-[#10a37f]/10 flex items-center justify-center mb-5">
                <span className="material-symbols-outlined text-[#10a37f] text-4xl" style={{ fontVariationSettings: "'FILL' 1" }}>mic</span>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Voice AI</h2>
              <p className="text-gray-500 text-sm max-w-xs mb-6">
                Tekan tombol mikrofon untuk mulai berbicara, atau ketik pertanyaan di kolom teks.
                Percakapan suara juga tersimpan di riwayat Chat.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left max-w-sm w-full">
                {[
                  { icon: "mic",       text: "Tekan mic → bicara → lepas" },
                  { icon: "keyboard",  text: "Atau ketik teks di bawah" },
                  { icon: "volume_up", text: "Jawaban bisa diputar sebagai audio" },
                  { icon: "chat",      text: "Riwayat juga muncul di Chat" },
                ].map((tip) => (
                  <div key={tip.icon} className="flex items-center gap-2 text-xs text-gray-500 bg-white rounded-xl px-3 py-2.5 border border-gray-100">
                    <span className="material-symbols-outlined text-[#10a37f] text-[16px]">{tip.icon}</span>
                    {tip.text}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Turn cards */}
          {voiceTurns.map((turn) => (
            <TurnCard
              key={turn.voice_turn_id}
              turn={turn}
              playing={playingTurnId === turn.voice_turn_id}
              onPlay={setPlayingTurnId}
            />
          ))}

          {/* Processing indicator */}
          {isProcessing && (
            <div className="flex gap-3 py-2">
              <div className="w-8 h-8 rounded-lg bg-[#10a37f] flex items-center justify-center flex-shrink-0 mt-1">
                <span className="material-symbols-outlined text-white text-[16px] animate-spin">refresh</span>
              </div>
              <div className="flex-1">
                <p className="text-xs font-semibold text-gray-400 mb-1">Assistant</p>
                <div className="bg-white border border-gray-100 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
                  <div className="flex items-center gap-2 text-[#10a37f]">
                    <span className="material-symbols-outlined text-[18px] animate-pulse">{statusInfo?.icon || "psychology"}</span>
                    <span className="text-sm font-medium">{statusInfo?.text}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div ref={endRef} />
        </div>
      </div>

      {/* Bottom controls */}
      <div className="flex-shrink-0 bg-white border-t border-gray-100 px-4 md:px-6 py-4">
        <div className="max-w-[700px] mx-auto">
          {/* Status bar */}
          {statusInfo?.text && phase !== "idle" && (
            <div className={`flex items-center gap-2 mb-3 text-sm font-medium ${statusInfo.color}`}>
              <span className={`material-symbols-outlined text-[18px] ${phase === "recording" ? "animate-pulse" : ""}`}>
                {statusInfo.icon}
              </span>
              <span className="flex-1">{statusInfo.text}</span>
              {phase === "error" && (
                <button onClick={() => setPhase("idle")} className="text-xs text-gray-400 hover:text-gray-600 underline whitespace-nowrap">Tutup</button>
              )}
            </div>
          )}

          <div className="flex items-end gap-3">
            {/* Text input */}
            <div className="flex-1 bg-gray-50 border border-gray-200 rounded-2xl px-4 py-2.5 focus-within:border-[#10a37f] focus-within:ring-2 focus-within:ring-[#10a37f]/10 transition-all">
              <textarea
                className="w-full bg-transparent border-none focus:ring-0 outline-none text-gray-900 placeholder:text-gray-400 text-sm resize-none max-h-[100px] leading-relaxed"
                placeholder="Ketik pesan..."
                value={textInput}
                disabled={isBusy}
                rows={1}
                onChange={(e) => {
                  setTextInput(e.target.value);
                  e.target.style.height = "auto";
                  e.target.style.height = Math.min(e.target.scrollHeight, 100) + "px";
                }}
                onKeyDown={handleTextInputKeyDown}
              />
            </div>

            {/* Send text */}
            <button
              onClick={sendTextAsVoice}
              disabled={!textInput.trim() || isBusy}
              className="w-11 h-11 flex items-center justify-center rounded-2xl bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
              title="Kirim teks"
            >
              <span className="material-symbols-outlined text-[20px]" style={{ fontVariationSettings: "'FILL' 1" }}>send</span>
            </button>

            {/* Mic button */}
            <button
              onClick={recording ? stopRecording : startRecording}
              disabled={isProcessing}
              className="flex-shrink-0 disabled:cursor-not-allowed"
              title={recording ? "Stop Recording" : "Mulai Merekam"}
            >
              <VoicePulse active={recording} />
            </button>
          </div>

          <p className="text-center text-[10px] text-gray-300 mt-3">
            {recording
              ? "Klik tombol merah untuk berhenti dan mengirim"
              : "Percakapan suara tersimpan di riwayat Chat · Jawaban otomatis diputar jika TTS aktif"}
          </p>
        </div>
      </div>
    </main>
  );
}
